import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useAdminApp } from '@/modules/admin/context/AdminAppContext';
import { copyToClipboard } from '@/shared/utils/exportUtils';
import AdminGitHubSection from '@/modules/admin/components/AdminGitHubSection';
import AdminTerminalCommandsSection from '@/modules/admin/components/AdminTerminalCommandsSection';
import AdminAppDomainSection from '@/modules/admin/components/AdminAppDomainSection';
import AdminDatabaseSection from '@/modules/admin/components/AdminDatabaseSection';
import AdminAuthenticationSection from '@/modules/admin/components/AdminAuthenticationSection';
import AdminBackendSection from '@/modules/admin/components/AdminBackendSection';
import AdminEmailSection from '@/modules/admin/components/AdminEmailSection';
import AdminEmailForwardingSection from '@/modules/admin/components/AdminEmailForwardingSection';
import AdminTwilioSection from '@/modules/admin/components/AdminTwilioSection';
import AdminPaymentsSection from '@/modules/admin/components/AdminPaymentsSection';
import AdminDeploymentSection from '@/modules/admin/components/AdminDeploymentSection';
import AdminUsageBillingSection from '@/modules/admin/components/AdminUsageBillingSection';
import AdminServicesSection from '@/modules/admin/components/AdminServicesSection';
import AdminCDNSection from '@/modules/admin/components/AdminCDNSection';
import AdminImagesSection from '@/modules/admin/components/AdminImagesSection';
import AdminStorageSection from '@/modules/admin/components/AdminStorageSection';
import AdminRedisSection from '@/modules/admin/components/AdminRedisSection';
import AdminOutpostSection from '@/modules/admin/components/AdminOutpostSection';
import AdminBestPracticesSection from '@/modules/admin/components/AdminBestPracticesSection';
import AdminDocumentSigningSection from '@/modules/admin/components/AdminDocumentSigningSection';
import AdminCustomerSupportSection from '@/modules/admin/components/AdminCustomerSupportSection';

export default function AdminAppSettingsPage() {
  const { appId } = useParams();
  const { authFetch } = useAuthContext();
  const { app, loadApp, showToast, handleDeleteApp } = useAdminApp();
  const [copied, setCopied] = useState(false);
  const [showPwaGuide, setShowPwaGuide] = useState(false);

  const techStack = `Frontend:
- React (Vite) + JavaScript
- Tailwind CSS v4
- TanStack Query (data fetching/caching)
- Zod (runtime validation at API boundaries)
- Deployed on Vercel`;

  const handleCopy = async () => {
    const result = await copyToClipboard(techStack);
    if (result.success) {
      setCopied(true);
      if (showToast) {
        showToast('Tech stack copied to clipboard', 'success', 2000);
      }
      setTimeout(() => setCopied(false), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy tech stack', 'error');
      }
    }
  };

  return (
    <div className="mt-6">
      {/* Tech Stack Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span>⚙️</span>
            <span>Tech Stack</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Copy the tech stack information for this app
          </p>
        </div>

        <div className="flex items-start justify-between gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group">
          <div className="flex-1 min-w-0">
            <code className="text-sm text-gray-800 font-mono bg-white px-3 py-2 rounded border border-gray-300 block whitespace-pre-wrap">
              {techStack}
            </code>
          </div>
          <button
            onClick={handleCopy}
            className="ml-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 flex-shrink-0"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* PWA Guide Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="mb-4">
          <button
            onClick={() => setShowPwaGuide(!showPwaGuide)}
            className="w-full flex items-center justify-between text-left"
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span>📱</span>
                <span>PWA Guide</span>
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Convert Vite React app to a Progressive Web App
              </p>
            </div>
            <span className="text-gray-500 text-sm">
              {showPwaGuide ? '▼' : '▶'}
            </span>
          </button>
        </div>

        {showPwaGuide && (
          <div className="space-y-4 text-sm text-gray-700">
            {/* Step 1 */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="font-semibold text-gray-900 mb-2">1. Install vite-plugin-pwa</p>
              <code className="text-xs text-gray-800 font-mono bg-white px-2 py-1 rounded border border-gray-200 block">
                npm install -D vite-plugin-pwa
              </code>
              <p className="text-xs text-gray-500 mt-2">Configure in <code className="bg-gray-100 px-1 rounded">vite.config.js</code></p>
            </div>

            {/* Step 2 */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="font-semibold text-gray-900 mb-2">2. Create manifest</p>
              <p className="text-xs text-gray-600">Configure in VitePWA plugin options:</p>
              <ul className="text-xs text-gray-600 mt-1 list-disc list-inside space-y-0.5">
                <li>App name and short name</li>
                <li>Icons (192x192, 512x512)</li>
                <li>Theme color and background color</li>
                <li>Display mode (standalone)</li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="font-semibold text-gray-900 mb-2">3. Add icons in public/ folder</p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                <li><code className="bg-gray-100 px-1 rounded">pwa-192x192.png</code></li>
                <li><code className="bg-gray-100 px-1 rounded">pwa-512x512.png</code></li>
                <li><code className="bg-gray-100 px-1 rounded">apple-touch-icon.png</code></li>
              </ul>
              <p className="text-xs text-gray-500 mt-2 italic">
                Tip: Create placeholder icons with a temporary Node.js script using sharp to generate SVG-to-PNG icons with a gradient background and app initial, then delete the script.
              </p>
            </div>

            {/* Step 4 */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="font-semibold text-gray-900 mb-2">4. Add meta tags in index.html</p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                <li><code className="bg-gray-100 px-1 rounded">&lt;meta name="theme-color"&gt;</code></li>
                <li><code className="bg-gray-100 px-1 rounded">&lt;meta name="description"&gt;</code></li>
                <li><code className="bg-gray-100 px-1 rounded">&lt;link rel="apple-touch-icon"&gt;</code></li>
              </ul>
            </div>

            {/* Step 5 */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="font-semibold text-gray-900 mb-2">5. Add install banner component</p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                <li>Use <code className="bg-gray-100 px-1 rounded">beforeinstallprompt</code> event for Android/Chrome</li>
                <li>Handle iOS separately (manual "Add to Home Screen" instructions)</li>
                <li>Use the iOS share icon (square with arrow pointing up) - e.g. <code className="bg-gray-100 px-1 rounded">IoShareOutline</code> from react-icons</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* App Best Practices Section */}
      <AdminBestPracticesSection
        appId={appId}
      />

      {/* External Services Section */}
      <AdminServicesSection
        appId={appId}
        showToast={showToast}
      />

      {/* GitHub Section */}
      <AdminGitHubSection
        app={app}
        appId={appId}
        onRefresh={() => loadApp(appId)}
        authFetch={authFetch}
        showToast={showToast}
      />

      {/* Terminal Commands Section */}
      <AdminTerminalCommandsSection
        appId={appId}
        showToast={showToast}
      />

      {/* Deployment URLs & Setup Section */}
      <AdminDeploymentSection
        app={app}
        appId={appId}
        showToast={showToast}
      />

      {/* CDN Section */}
      <AdminCDNSection
        appId={appId}
        showToast={showToast}
      />

      {/* Images Section */}
      <AdminImagesSection />

      {/* App Domain Section */}
      <AdminAppDomainSection
        appId={appId}
        showToast={showToast}
      />

      {/* Database Section */}
      <AdminDatabaseSection
        appId={appId}
        showToast={showToast}
      />

      {/* Storage Section */}
      <AdminStorageSection
        appId={appId}
      />

      {/* Redis Section */}
      <AdminRedisSection
        appId={appId}
      />

      {/* Webhooks Section */}
      <AdminOutpostSection
        appId={appId}
      />

      {/* Authentication Section */}
      <AdminAuthenticationSection
        appId={appId}
      />

      {/* Backend Functionality Section */}
      <AdminBackendSection
        appId={appId}
      />

      {/* Email Section */}
      <AdminEmailSection
        appId={appId}
      />

      {/* Email Forwarding Section */}
      <AdminEmailForwardingSection
        appId={appId}
      />

      {/* Twilio Section */}
      <AdminTwilioSection
        appId={appId}
      />

      {/* Customer Support Section */}
      <AdminCustomerSupportSection
        appId={appId}
      />

      {/* Payments Section */}
      <AdminPaymentsSection
        appId={appId}
      />

      {/* Document Signing Section */}
      <AdminDocumentSigningSection
        appId={appId}
      />

      {/* Usage Billing Section */}
      <AdminUsageBillingSection
        app={app}
        appId={appId}
        authFetch={authFetch}
        showToast={showToast}
        onUpdate={() => loadApp(appId)}
      />

      {/* Danger Zone */}
      <div className="bg-white rounded-lg border-2 border-red-200 p-6 mt-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-red-600 flex items-center gap-2">
            <span>⚠️</span>
            <span>Danger Zone</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Irreversible and destructive actions
          </p>
        </div>

        <div className="border-t border-red-100 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Delete this app</h3>
              <p className="text-sm text-gray-500">
                Permanently delete this app and all associated data including notes, charges, and repositories.
              </p>
            </div>
            <button
              onClick={handleDeleteApp}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm whitespace-nowrap ml-4"
            >
              🗑️ Delete App
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
